import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core';
import CustomHeader from 'components/CustomHeader/CustomHeader';
import Spinner from 'components/UI/Spinner/Spinner';
import { fetchSharingAgreementsReport } from 'components/SharingAgreementsReport/fetchSharingAgreementsReport';
import { SHARING_AGREEMENTS } from 'constants/Constants';
import { setCurrentPage } from 'store/actions';
import { downloadVisibilitySharingCsv } from './exportVisibilitySharingCsv';
import StatusChip from './StatusChip';
import {
    SHARING_REQUEST_FILTERS,
    SHARING_REQUEST_STATUS,
    countByStatus,
    emptyFilterMessage,
    filterSharingRequests,
    rowsMatchingCarrier,
    sharingDetailLocation,
    sortSharingRequests
} from './sharingRequestModel';
import { fetchVisibilitySharing } from './visibilitySharingApi';
import styles from './VisibilitySharing.module.css';

export const VisibilitySharingInboxBase = ({
    history,
    location,
    setCurrentPage,
    accountUuid,
    selectedAccountId
}) => {
    const restoredFilter = SHARING_REQUEST_FILTERS.includes(location?.state?.statusFilter)
        ? location.state.statusFilter
        : SHARING_REQUEST_STATUS.PENDING;
    const [statusFilter, setStatusFilter] = useState(restoredFilter);
    const [searchValue, setSearchValue] = useState(location?.state?.searchValue || '');
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    const load = () => {
        setLoading(true);
        setLoadFailed(false);
        return fetchVisibilitySharing({
            accountId: selectedAccountId || accountUuid
        })
            .then((result) => {
                setRows(sortSharingRequests(result.rows || []));
                setErrors(result.errors || []);
                setLoadFailed((result.rows || []).length === 0 && (result.errors || []).length > 0);
            })
            .catch(() => {
                setRows([]);
                setErrors([{ message: 'Sharing agreements could not be loaded.' }]);
                setLoadFailed(true);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setCurrentPage(SHARING_AGREEMENTS);
    }, [setCurrentPage]);

    useEffect(() => {
        load();
    }, [accountUuid, selectedAccountId]);

    const searchedRows = useMemo(() => rowsMatchingCarrier(rows, searchValue), [rows, searchValue]);
    const counts = useMemo(() => countByStatus(searchedRows), [searchedRows]);
    const visibleRows = useMemo(
        () => filterSharingRequests(searchedRows, { status: statusFilter, carrier: '' }),
        [searchedRows, statusFilter]
    );
    const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    useEffect(() => {
        setPage(0);
    }, [statusFilter, searchValue]);

    const openDetail = (row) => {
        history.push(sharingDetailLocation(row, { statusFilter, searchValue }));
    };

    const exportCsv = () => {
        setExporting(true);
        setExportError('');
        fetchSharingAgreementsReport({
            carrier: searchValue,
            accountUuid: selectedAccountId || accountUuid
        })
            .then((result) => {
                downloadVisibilitySharingCsv(result.items || [], 'all');
                if ((result.errors || []).length > 0) {
                    setExportError('Some sharing agreements could not be included in the export.');
                }
            })
            .catch(() => setExportError('The export could not be created.'))
            .finally(() => setExporting(false));
    };

    const emptyMessage = searchValue.trim()
        ? 'No carriers match that name.'
        : emptyFilterMessage(statusFilter);

    return (
        <>
            <CustomHeader title="SHARING AGREEMENTS" />
            <div className={styles.page}>
                <Typography component="h1" variant="h5" id="pageTitle">
                    Sharing agreements
                </Typography>
                <p className={styles.intro}>Requests from carriers to share visibility data.</p>
                <div className={styles.toolbar}>
                    <div
                        className={styles.filters}
                        role="tablist"
                        aria-label="Sharing agreement status"
                    >
                        {SHARING_REQUEST_FILTERS.map((filter) => (
                            <button
                                key={filter}
                                type="button"
                                role="tab"
                                aria-selected={statusFilter === filter}
                                className={`${styles.filterButton} ${
                                    statusFilter === filter ? styles.filterButtonSelected : ''
                                }`}
                                onClick={() => setStatusFilter(filter)}
                            >
                                {filter} ({counts[filter] || 0})
                            </button>
                        ))}
                    </div>
                    <TextField
                        className={styles.search}
                        id="sharing-agreements-search-input"
                        label="Search by carrier"
                        value={searchValue}
                        onChange={(event) => setSearchValue(event.target.value)}
                        variant="outlined"
                        size="small"
                    />
                    <Button
                        id="sharing-agreements-export-csv"
                        variant="outlined"
                        onClick={exportCsv}
                        disabled={exporting || loading}
                    >
                        Export CSV
                    </Button>
                </div>
                {errors.length > 0 && !loadFailed ? (
                    <div className={styles.banner} role="alert">
                        Some sharing agreements could not be loaded. The list shows the agreements
                        that are available.
                    </div>
                ) : null}
                {exportError ? (
                    <div className={styles.banner} role="alert">
                        {exportError}
                    </div>
                ) : null}
                <Paper>
                    {loading ? (
                        <Spinner />
                    ) : loadFailed ? (
                        <div className={styles.empty} role="status">
                            Sharing agreements could not be loaded.
                        </div>
                    ) : visibleRows.length === 0 ? (
                        <div className={styles.empty} role="status">
                            {emptyMessage}
                        </div>
                    ) : (
                        <div className={styles.tableWrap}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Carrier</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Direction</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Sharing</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pageRows.map((row) => (
                                        <TableRow key={`${row.source}-${row.id}`}>
                                            <TableCell>{row.carrierName}</TableCell>
                                            <TableCell>{row.typeLabel}</TableCell>
                                            <TableCell>{row.direction || ''}</TableCell>
                                            <TableCell>
                                                <StatusChip
                                                    status={row.status}
                                                    pendingGroupCount={row.pendingGroupCount}
                                                />
                                            </TableCell>
                                            <TableCell>{row.summary}</TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    onClick={() => openDetail(row)}
                                                >
                                                    Review
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={visibleRows.length}
                                page={page}
                                onChangePage={(event, nextPage) => setPage(nextPage)}
                                rowsPerPage={rowsPerPage}
                                onChangeRowsPerPage={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[10, 15, 20]}
                            />
                        </div>
                    )}
                </Paper>
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    accountUuid: state.session?.sessionValue?.accountUuid,
    selectedAccountId: state.accounts?.selectedAccount?.id
});

export default withRouter(
    connect(mapStateToProps, (dispatch) => ({
        setCurrentPage: (page) => dispatch(setCurrentPage(page))
    }))(VisibilitySharingInboxBase)
);
